/* UninstallPage — the post-uninstall churn page.
 *
 * Deliberately borderless: the only strokes on the page are the two
 * fields, the alert, and the footer rule. No card, no Surface — the
 * visitor has no session, so app furniture would imply they are still
 * inside a product they just removed. See the component docblock.
 *
 * Two literal values below (6px, 10px). Neither has a token, and both
 * already appear with the same justification in
 * packages/client-ui/src/components/FeedbackModal/FeedbackModal.module.css.
 * Matching the existing literal beats inventing a nearby token value. */

/* `#root` is a flex column at viewport height with `overflow: hidden`
 * (see global.css) and routes own their own scroll container, so this
 * one takes the remaining space and scrolls. `min-height: 0` is what
 * lets it actually shrink inside the flex parent rather than overflow. */
.page {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  background: var(--bg-primary);
  /* 56px has no token — the scale stops at 40. Composed from the
   * nearest step rather than hardcoded so it tracks a scale change. */
  padding-block: calc(var(--spacing-28) * 2) var(--spacing-28);
  /* Minimum gutter so the column never touches the viewport edge. On a
   * desktop window the real gutter is whatever centring 560px leaves. */
  padding-inline: var(--spacing-16);
}

/* Left-aligned, horizontally centred, never vertically centred: the
 * headline lands in the same place regardless of viewport height. */
.column {
  display: flex;
  flex-direction: column;
  max-width: 560px;
  margin-inline: auto;
}

/* 22px is the drawn wordmark height, between Logo's `sm` (20) and `md`
 * (24) buckets — so the height is set here rather than by a size prop. */
.logo {
  height: 22px;
  width: auto;
  align-self: flex-start;
  margin-bottom: var(--spacing-24);
}

.headline {
  margin: 0;
}

.body {
  margin: var(--spacing-8) 0 0;
}

.ask {
  margin: var(--spacing-20) 0 0;
}

/* Replaces the ask (not the headline or body — those are the page's
 * factual content, and removing them would leave a floating thank-you
 * with no context). No green and no icon: --text-success exists but is
 * green, which means "connected" everywhere else in the product. */
.success {
  margin: var(--spacing-28) 0 0;
}

.success:focus {
  /* Focus is moved here programmatically on success so a keyboard user
   * is not stranded on a button that no longer exists. It is a
   * scripted destination, not an interactive control, so it takes no
   * visible ring. */
  outline: none;
}

.form {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-20);
  margin-top: var(--spacing-28);
}

/* Kept as a block (not flex): a flex `fieldset` lays its `legend` out
 * inconsistently across engines, and the 8px legend-to-group gap is
 * cheaper as a margin than as a flex gap. `min-inline-size: 0`
 * overrides the fieldset's intrinsic min-content width, which would
 * otherwise stop the column from shrinking on a narrow window. */
.fieldset {
  margin: 0;
  padding: 0;
  border: 0;
  min-inline-size: 0;
}

.legend {
  padding: 0;
  margin-bottom: var(--spacing-8);
  font: var(--text-body-medium);
  color: var(--text-primary);
}

/* The group is `inline-flex`, so inside the block fieldset it already
 * shrinks to its content. The cap only matters on a narrow window,
 * where it keeps the row from pushing the column wider than the page. */
.tabs {
  max-width: 100%;
}

.field {
  display: flex;
  flex-direction: column;
  /* 6px literal: no --spacing-6 token; the tightest readable
   * label↔control gap, same as FeedbackModal.module.css `.field`. */
  gap: 6px;
}

.label {
  font: var(--text-body-medium);
  color: var(--text-primary);
}

/* No Textarea primitive exists. These rules are lifted from
 * FeedbackModal.module.css; only `min-height` differs (144px — the
 * message field is this page's visual focus). Promote to client-ui
 * only if a third consumer appears. 10px padding is the same
 * no-token literal that file already carries. */
.textarea {
  width: 100%;
  box-sizing: border-box;
  resize: vertical;
  min-height: 144px;
  padding: 10px;
  /* 14/20, not 16/24: at 16px the fields read louder than the sentence
   * asking the question, which is backwards on a page whose focus is
   * the question. Matches Input size="sm" on the email field. */
  font: var(--text-body-regular);
  color: var(--text-primary);
  background: var(--bg-primary);
  border: 1px solid var(--stroke-neutral);
  border-radius: var(--radius-8);
}

.textarea::placeholder {
  color: var(--text-tertiary);
}

/* Copies Input's focus treatment exactly — this is the one control on
 * the page with no primitive behind it, so the ring has to be matched
 * by hand or it drifts from the email field beside it. */
.textarea:focus {
  outline: none;
  border-color: var(--brand);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--brand) 18%, transparent);
}

/* Value stays readable while sending — the fill steps up, the text
 * does not disappear. */
.textarea:disabled {
  background: var(--bg-secondary);
  color: var(--text-tertiary);
  cursor: not-allowed;
}

.counter {
  margin: 0;
}

/* No fill — the border and the icon carry it. Nothing here is signalled
 * by colour alone: the row always has an icon and a whole sentence. */
.alert {
  display: flex;
  gap: var(--spacing-8);
  padding: var(--spacing-12);
  border: 1px solid var(--text-negative);
  border-radius: var(--radius-8);
}

/* Optical alignment with the first line of the sentence beside it. */
.alertIcon {
  flex: none;
  margin-top: 2px;
}

/* Flex row so the button sizes to its label instead of stretching to
 * the column's width the way a bare flex child would. */
.submitRow {
  display: flex;
}

.privacyNote {
  margin: 0;
  max-width: 64ch;
}

.footer {
  margin-top: var(--spacing-28);
  padding-top: var(--spacing-16);
  border-top: 1px solid var(--stroke-neutral);
  display: flex;
  flex-wrap: wrap;
  /* 6px literal — same no-token gap as `.field`. */
  gap: 6px;
  align-items: baseline;
}

/* sr-only clip pattern, copied from FeedbackModal.module.css. Never
 * `display: none` alone and never removed from the body: the server
 * drops filled-honeypot submissions BEFORE rate limiting, so this
 * field is what stops bots exhausting the surface quota. */
.honeypot {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* Insurance for browser zoom and narrow windows, not a designed mobile
 * layout — Chrome only opens this URL on a desktop uninstall. The tab
 * row needs ~423px and `Tab` sets `white-space: nowrap`, so below
 * roughly 470px of column width it has to stack rather than overflow.
 * The primitive's roving tabindex and arrow keys keep working: its
 * ArrowUp/ArrowDown handling is already wired alongside left/right. */
@media (max-width: 600px) {
  .page {
    padding-block-start: var(--spacing-28);
  }

  .tabs {
    flex-direction: column;
  }

  .tabs button {
    width: 100%;
    height: 32px;
    justify-content: flex-start;
  }
}
